import React, { useEffect, useState } from "react";
import { Alert, Box, Chip, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { getSupportAnalytics, SupportAnalyticsResponse } from "../services/supportService";
import { CSAT_REASON_LABELS, QUEUE_LABELS } from "../constants/support";

/** Support v2 Phase 2 analytics (§12): live desk numbers and a 7 / 30 / 90-day report. */
const dash = (v: number | null | undefined, suffix = "") => (v == null ? "—" : `${v}${suffix}`);
const AGE_LABELS: Record<string, string> = { under_1h: "< 1 h", "1_4h": "1–4 h", "4_24h": "4–24 h", "1_3d": "1–3 days", over_3d: "> 3 days" };

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
);

const Counts: React.FC<{ title: string; data: Record<string, number>; labels?: Record<string, string> }> = ({ title, data, labels }) => {
    const rows = Object.entries(data).sort((a, b) => b[1] - a[1]);
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{title}</Typography>
            {rows.length === 0 ? <Typography variant="body2" color="text.secondary">None</Typography> : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {rows.map(([k, n]) => <Chip key={k} size="small" label={`${labels?.[k] ?? k}: ${n}`} />)}
                </Stack>
            )}
        </Box>
    );
};

const SupportAnalytics: React.FC = () => {
    const [days, setDays] = useState(7);
    const [data, setData] = useState<SupportAnalyticsResponse | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setError(false);
        getSupportAnalytics(days).then((r) => setData(r.data)).catch(() => setError(true));
    }, [days]);

    const live = data?.live, p = data?.period;
    const busiest = p ? p.volume.byHourIst.reduce((best, n, h) => (n > p.volume.byHourIst[best] ? h : best), 0) : 0;
    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Support analytics</Typography>
                <Select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))} inputProps={{ "aria-label": "Report period" }}>
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
            </Stack>
            {error && <Alert severity="error">Could not load support analytics.</Alert>}
            {live && p && (
                <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Right now</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Tile label="Open tickets" value={p.backlog.open} />
                        <Tile label="SLA at risk (30 min)" value={live.slaAtRisk} />
                        <Tile label="Active SOS" value={live.activeSos} />
                        <Tile label="Agents on duty" value={live.onDutyAgents} />
                    </Stack>
                    <Counts title="Open by queue" data={live.openByQueue} labels={QUEUE_LABELS} />
                    <Counts title="Backlog age" data={p.backlog.byAge} labels={AGE_LABELS} />

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3 }}>Last {p.days} days</Typography>
                    {p.capped && <Alert severity="warning" sx={{ my: 1 }}>Only the first 20,000 tickets were counted.</Alert>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Tile label="Tickets" value={p.volume.total} sub={`busiest hour ${busiest}:00 IST`} />
                        <Tile label="First reply" value={dash(p.firstReply.medianMin, " min")} sub={`p90 ${dash(p.firstReply.p90Min, " min")} · SLA met ${dash(p.firstReply.slaMetPct, "%")}`} />
                        <Tile label="Resolution" value={dash(p.resolution.medianMin, " min")} sub={`p90 ${dash(p.resolution.p90Min, " min")} · SLA met ${dash(p.resolution.slaMetPct, "%")}`} />
                        <Tile label="Reopen rate" value={dash(p.reopenRatePct, "%")} />
                        <Tile label="CSAT" value={dash(p.csat.avg, "/5")} sub={`${p.csat.count} ratings · ${p.csat.low} low`} />
                        <Tile label="Repeat contacts" value={p.repeatContacts} />
                        <Tile label="Bot deflection" value={dash(p.bot.deflectionPct, "%")} sub={`${p.bot.solved} solved · ${p.bot.handedOff} handed off`} />
                        <Tile label="SOS" value={p.sos.count} sub={`ack median ${dash(p.sos.ackMedianSec, " s")} · ${p.sos.acknowledged} acknowledged`} />
                    </Stack>
                    <Counts title="By category" data={p.volume.byCategory} />
                    <Counts title="By queue" data={p.volume.byQueue} labels={QUEUE_LABELS} />
                    <Counts title="By city" data={p.volume.byCity} />
                    <Counts title="Low-rating reasons" data={p.csat.reasons} labels={CSAT_REASON_LABELS} />
                    <Counts title="Bot outcomes" data={p.bot.byOutcome} />
                    <Counts title="SOS outcomes" data={p.sos.byOutcome} />
                    <Counts title="Most-complained drivers (misconduct)" data={Object.fromEntries(p.topDrivers.map((d) => [d.driverId, d.complaints]))} />
                </>
            )}
        </Box>
    );
};

export default SupportAnalytics;
